import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import useFocusTrap from "../../../hooks/useFocusTrap";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { bdcApi } from "../../../services/bdcApi";
import { isSafeUrl } from "../../../components/SafeLink";

const STATUS_TABS = [
  { value: "",         label: "All" },
  { value: "PENDING",  label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "PAID",     label: "Paid" },
  { value: "FROZEN",   label: "Frozen" },
];

const STATUS_CLASS = {
  PENDING:  "s-rev",
  APPROVED: "s-info",
  PAID:     "s-active",
  FROZEN:   "s-rev",
};

const TAX_DOC_TYPES = ["T4A", "T5018", "NR4", "OTHER"];
const MIN_NOTES = 10;
const CONFIRM_PHRASE = "PAID";

const backdropStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
  zIndex: 1000, display: "grid", placeItems: "center", padding: 20,
};
const dialogStyle = {
  background: "var(--bg-card)", border: "1px solid var(--bd)",
  borderRadius: "var(--r-md)", padding: 20, maxWidth: 560, width: "100%",
  maxHeight: "90vh", overflow: "auto",
  boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
};

function PayoutAdmin() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const [paidDialog, setPaidDialog] = useState(null);
  const [taxDocUrl, setTaxDocUrl] = useState("");
  const [taxDocType, setTaxDocType] = useState("T5018");
  const [paymentRef, setPaymentRef] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const dialogRef = useFocusTrap({
    active: !!paidDialog,
    onClose: () => !submitting && setPaidDialog(null),
  });

  function fetchList() {
    setLoading(true);
    const params = { page, limit: 25 };
    if (statusFilter) params.status = statusFilter;
    bdcApi.adListAllPayouts(params)
      .then((res) => {
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  async function transition(action, row, body = {}) {
    setSubmitting(true);
    try {
      const fn = {
        APPROVE: bdcApi.adApprovePayout,
        FREEZE: bdcApi.adFreezePayout,
        RELEASE: bdcApi.adReleasePayout,
        PAID: bdcApi.adMarkPaid,
      }[action];
      await fn(row.id, body);
      setFlash({ type: "info", message: `Payout #${row.id} → ${action}` });
      fetchList();
    } catch (err) {
      setFlash({
        type: "danger",
        message: err?.response?.data?.message || err.message || "Transition failed",
      });
    } finally {
      setSubmitting(false);
    }
  }

  function quickTransition(row, action) {
    if (!window.confirm(`${action.toLowerCase()} payout #${row.id}?`)) return;
    transition(action, row, { notes: `[AD] ${action} via PayoutAdmin` });
  }

  function openMarkPaid(row) {
    setPaidDialog(row);
    setTaxDocUrl(row.tax_doc_url || "");
    setTaxDocType(row.tax_doc_type || "T5018");
    setPaymentRef(row.payment_reference || "");
    setNotes("");
    setConfirmPhrase("");
  }

  async function submitMarkPaid(ev) {
    ev.preventDefault();
    const trimmedUrl = taxDocUrl.trim();
    if (!trimmedUrl || !TAX_DOC_TYPES.includes(taxDocType)) {
      setFlash({ type: "danger", message: "Tax doc URL + type required before PAID." });
      return;
    }
    if (!isSafeUrl(trimmedUrl)) {
      setFlash({ type: "danger", message: "Tax doc URL must be http(s) or mailto — rejected by scheme allowlist." });
      return;
    }
    if (notes.trim().length < MIN_NOTES) {
      setFlash({ type: "danger", message: `Notes must be at least ${MIN_NOTES} characters.` });
      return;
    }
    if (confirmPhrase !== CONFIRM_PHRASE) {
      setFlash({ type: "danger", message: `Type "${CONFIRM_PHRASE}" to confirm the irreversible transition.` });
      return;
    }
    await transition("PAID", paidDialog, {
      tax_doc_url: trimmedUrl,
      tax_doc_type: taxDocType,
      payment_reference: paymentRef.trim() || undefined,
      notes: notes.trim(),
    });
    setPaidDialog(null);
  }

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title="Payouts (AD)"
        subtitle={<><span className="mono">{total}</span><span>record{total === 1 ? "" : "s"}</span></>}
      />

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        System-wide payout queue. PAID transitions require a CRA tax-slip URL + type (T4A / T5018 / NR4 / OTHER) on the row.
      </p>

      {flash && <Alert tone={flash.type === "ok" ? "info" : flash.type}>{flash.message}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      <div className="toolbar">
        <div className="toolbar__pills">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value || "all"}
              type="button"
              className={`toolbar__pill${statusFilter === t.value ? " toolbar__pill--on" : ""}`}
              onClick={() => { setStatusFilter(t.value); setPage(1); }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <Empty>No payouts match this filter.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th>
                  <th>BDC</th>
                  <th>Period</th>
                  <th className="r">Amount</th>
                  <th>Status</th>
                  <th>Tax doc</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const statusUpper = String(p.status || "").toUpperCase();
                  return (
                    <tr key={p.id}>
                      <td className="mono">#{p.id}</td>
                      <td className="mono">#{p.bdc_user_id}</td>
                      <td className="mono">{String(p.period_start).slice(0, 10)} → {String(p.period_end).slice(0, 10)}</td>
                      <td className="r"><strong>{p.currency || "CAD"} {Number(p.total_amount).toFixed(2)}</strong></td>
                      <td>
                        <span className={`tbl-status ${STATUS_CLASS[statusUpper] || "s-mute"}`}>{p.status}</span>
                      </td>
                      <td>
                        {p.tax_doc_type
                          ? <span style={{ color: "var(--pos-700)", fontFamily: "var(--f-mono)", fontSize: "var(--t-xs)" }}>{p.tax_doc_type} ✓</span>
                          : <span style={{ color: "var(--neg-700)", fontFamily: "var(--f-mono)", fontSize: "var(--t-xs)" }}>missing</span>}
                      </td>
                      <td className="muted">{formatAuditTimestamp(p.createdAt)}</td>
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          {p.status === "PENDING" && (
                            <Btn size="sm" onClick={() => quickTransition(p, "APPROVE")} disabled={submitting}>Approve</Btn>
                          )}
                          {p.status === "APPROVED" && (
                            <>
                              <Btn variant="primary" size="sm" onClick={() => openMarkPaid(p)} disabled={submitting}>Mark paid</Btn>
                              <Btn size="sm" onClick={() => quickTransition(p, "FREEZE")} disabled={submitting}>Freeze</Btn>
                            </>
                          )}
                          {p.status === "FROZEN" && (
                            <Btn size="sm" onClick={() => quickTransition(p, "RELEASE")} disabled={submitting}>Release</Btn>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>Page {page} of {totalPages}</span>
          <Btn size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}

      {paidDialog && (
        <div
          style={backdropStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ad-payout-dialog-title"
          ref={dialogRef}
        >
          <form onSubmit={submitMarkPaid} style={dialogStyle}>
            <h3 id="ad-payout-dialog-title" style={{ fontSize: "var(--t-xl)", fontWeight: 600, margin: "0 0 8px" }}>
              Mark payout #{paidDialog.id} PAID
            </h3>
            <p style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)", margin: "0 0 16px" }}>
              CRA requires a tax slip on every payout before PAID — T4A for individuals, T5018 for contractors, NR4 for non-resident payments.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field
                label="Tax slip URL (Azure Blob or external)"
                name="tax_doc_url"
                type="url"
                value={taxDocUrl}
                onChange={(e) => setTaxDocUrl(e.target.value)}
                required
                disabled={submitting}
              />
              <Field
                as="select"
                label="Tax slip type"
                name="tax_doc_type"
                value={taxDocType}
                onChange={(e) => setTaxDocType(e.target.value)}
                disabled={submitting}
              >
                {TAX_DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </Field>
              <Field
                label="Payment reference (optional)"
                name="payment_reference"
                type="text"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                placeholder="WT-2026-XXXX"
                maxLength={128}
                disabled={submitting}
              />

              <div className="field">
                <label className="field__label" htmlFor="ad-payout-notes">
                  Notes (≥ {MIN_NOTES} chars — audited)
                </label>
                <textarea
                  id="ad-payout-notes"
                  className="field__textarea"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  required
                  minLength={MIN_NOTES}
                  disabled={submitting}
                />
                <span className="field__hint">{notes.trim().length} / {MIN_NOTES}</span>
              </div>

              <Field
                label={<>Type <strong>{CONFIRM_PHRASE}</strong> to confirm (irreversible)</>}
                name="confirm_phrase"
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                autoComplete="off"
                required
                disabled={submitting}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <Btn type="button" onClick={() => setPaidDialog(null)} disabled={submitting}>Cancel</Btn>
              <Btn
                variant="danger"
                type="submit"
                disabled={
                  submitting ||
                  notes.trim().length < MIN_NOTES ||
                  !taxDocUrl ||
                  confirmPhrase !== CONFIRM_PHRASE
                }
              >
                {submitting ? "Submitting…" : "Confirm PAID"}
              </Btn>
            </div>
          </form>
        </div>
      )}
    </PageShell>
  );
}

export default PayoutAdmin;
